import { useEffect, useEffectEvent, useRef } from 'react';

import { useAtom, useAtomValue } from 'jotai';
import toast from 'react-hot-toast';

import { addDismissal, filterUndismissed } from '@cfa/pathway-fe-shared/alerts';

import { useAuth } from '@/hooks/useAuth';

import { getNameFromLanguage } from '@/utils/language';

import ToastMessageBlock from '@/components/Toasts/SuccessToast';

import AlertLink from './AlertLink';
import { alertMessageBlockDismissalsAtom } from './state';
import { useAlertsToShow } from './useAlertsToShow';

import { userPrefsAtom } from '@/atoms/userPrefs';

const alertToastId = (alertId: string) => `ld-alert:${alertId}`;

/**
 * Shows LaunchDarkly `messageBlock` alerts as persistent toasts in the app
 * `<Toaster>`, so they stack with regular toasts. Dismissing one records it
 * for the current user on this device and it never shows again.
 *
 * Pass `enabled: false` where the app Toaster isn't mounted (e.g. headless
 * views) so any shown blocks are removed. Native renders its own.
 */
export const useAlertMessageBlocks = ({ enabled }: { enabled: boolean }) => {
  const { messageBlocks } = useAlertsToShow();
  const { data: auth } = useAuth();
  const userId = auth?.userId ?? '';
  const [dismissals, setDismissals] = useAtom(alertMessageBlockDismissalsAtom);
  const { language } = useAtomValue(userPrefsAtom);

  const eligible =
    enabled && !window.native.isNative
      ? filterUndismissed(messageBlocks, dismissals, userId)
      : [];

  // Toast ids currently shown by this hook, so blocks that stop being eligible
  // (flag change, dismissal, user change) can be removed.
  const shownToastIdsRef = useRef<string[]>([]);

  const syncToasts = useEffectEvent(() => {
    const nextToastIds: string[] = [];

    eligible.forEach(alert => {
      const alertId = alert.id;
      if (!alertId) return;
      const toastId = alertToastId(alertId);
      nextToastIds.push(toastId);

      toast.custom(
        toastObj => (
          <ToastMessageBlock
            id={toastObj.id}
            onDismiss={() =>
              setDismissals(prev => addDismissal(prev, userId, alertId))
            }
            severity="info"
            title={
              alert.title
                ? getNameFromLanguage(alert.title, language)
                : undefined
            }
          >
            {getNameFromLanguage(alert.message, language)}{' '}
            {alert.link && <AlertLink link={alert.link} language={language} />}
          </ToastMessageBlock>
        ),
        { id: toastId, duration: Infinity },
      );
    });

    shownToastIdsRef.current
      .filter(toastId => !nextToastIds.includes(toastId))
      .forEach(toastId => toast.remove(toastId));
    shownToastIdsRef.current = nextToastIds;
  });

  // Re-sync only when what would be shown changes, not on every render.
  const signature = JSON.stringify({ eligible, language, userId });

  useEffect(() => {
    syncToasts();
  }, [signature]);
};
